/* eslint-disable no-param-reassign */

import { isDesktop } from '../utils/isDesktop';
import { getFocusableElements } from '../utils/getFocusableElements';
import { getKey } from '../utils/getKey';
import { TOUJOU_EVENT_MAIN_NAV_SECOND_LEVEL_OPEN } from '../utils/constants';

/**
 * This class handles the functionality of the main and service navigation,
 * including opening/closing sub-menus, managing accessibility features
 * like keyboard navigation and ARIA attributes, and handling mouse
 * events on desktop.
 */
export class Nav {
  /** @type {HTMLElement} */
  navEl;

  /** @type {Array<HTMLLIElement>} */
  navListItems = [];

  /** @type {Array<HTMLLIElement | null>} */
  activeSubNavs = [];

  /** @type {HTMLElement} */
  burgerEl;

  /** @type {string} */
  burgerSelector = '.burger';

  /** @type {string} */
  navWrapSelector = '.wrap--navigation';

  /** @type {string} */
  navListSelector = '.wrap--navigation .navigation--main';

  /** @type {string} */
  listItemSelector = '.navigation-item';

  /** @type {string} */
  hasSubNavAttribute = 'data-has-subnav';

  /** @type {string} */
  isOpenAttribute = 'data-is-open';

  /** @type {string} */
  listItemLevelAttribute = 'data-nav-item-level';

  /** @type {string} */
  listItemChevronSelector = '.navigation-item__toggle-state';

  /** @type {string} */
  listSelector = '.navigation';

  /** @type {string} */
  isOpenOnHoverAttribute = 'data-is-open-on-hover';

  /** @type {number} */
  minWidthDesktop;

  /**
   * Constructs a new Nav instance.
   * @param {HTMLElement} navEl The navigation wrapper element.
   */
  constructor(navEl) {
    this.navEl = navEl;
    this.minWidthDesktop = parseInt(getComputedStyle(navEl).getPropertyValue('--main-nav-min-width-desktop'), 10) || 840;
    this._getNavListItems();
    this.burgerEl = document.querySelector(this.burgerSelector);

    if (this.navEl.hasAttribute(this.isOpenOnHoverAttribute)) {
      this.navListItems.forEach((navListItem) => {
        if (navListItem.hasSubNav) {
          navListItem.addEventListener('mouseenter', this._handleMouseEvent);
          navListItem.addEventListener('mouseleave', this._handleMouseEvent);
        }
      });
    }

    this.navEl.addEventListener('keydown', this._trapFocus);
    this.navEl.addEventListener('keyup', this._handleKeyUp);
  }

  /**
   * Retrieves all navigation list items and initializes their
   * properties and event listeners.
   * @private
   * @returns {void}
   */
  _getNavListItems = () => {
    this.navListItems = [...this.navEl.querySelectorAll(this.listItemSelector)];

    if (!this.navListItems) {
      // eslint-disable-next-line no-console
      console.warn(`TOUJOU: Navigation #${this.navEl.id} has no list items!`);
      return;
    }

    this.navListItems.forEach((listItem) => {
      listItem.hasSubNav = listItem.hasAttribute(this.hasSubNavAttribute);
      if (!listItem.hasSubNav) return;

      listItem.isOpen = false;
      listItem.level = listItem.getAttribute(this.listItemLevelAttribute);

      listItem.toggleEl = listItem.querySelector(this.listItemChevronSelector);
      listItem.toggleEl.addEventListener('click', () => {
        this._toggleListItemState(listItem);
      });
      listItem.addEventListener('keyup', this._handleKeyUp);
    });
  };

  /**
   * Handles mouse events (mouseenter, mouseleave) on navigation list items.
   * @private
   * @param {MouseEvent} event The mouse event object.
   * @returns {void}
   */
  _handleMouseEvent = (event) => {
    if (!isDesktop(this.minWidthDesktop)) {
      return;
    }

    if (event.type === 'mouseenter' || event.type === 'mouseleave') {
      this._toggleListItemState(event.currentTarget);
    }
  };

  /**
   * Trap focus on mobile.
   * @private
   * @param {KeyboardEvent} event The keyboard event object.
   * @returns {void}
   */
  _trapFocus = (event) => {
    if (isDesktop(this.minWidthDesktop)) return;

    const { isTab, isShiftTab } = getKey(event);

    if (!isTab && !isShiftTab) return;

    const focusableElements = getFocusableElements(document.querySelector('body'), `:is(${this.navListSelector}, .wrap--navigation .navigation--service) :is(a, button), ${this.burgerSelector}`);
    const firstFocusableElement = focusableElements.first;
    const lastFocusableElement = focusableElements.last;

    if (document.activeElement === lastFocusableElement && isTab) {
      event.preventDefault();
      firstFocusableElement.focus({ focusVisible: true });
    }

    if (document.activeElement === firstFocusableElement && isShiftTab) {
      event.preventDefault();
      lastFocusableElement.focus({ focusVisible: true });
    }
  };

  /**
   * Handles keyboard events (keyup) for closing menus with the Escape key.
   * @private
   * @param {KeyboardEvent} event The keyboard event object.
   * @returns {void}
   */
  _handleKeyUp = (event) => {
    const { isEscape } = getKey(event);
    if (isEscape) {
      const listItem = this.activeSubNavs.pop();

      event.stopPropagation();

      if (!listItem) {
        if (this.burgerEl.getAttribute('aria-pressed') !== 'true') return;

        this.burgerEl.click();
      } else {
        this._toggleListItemState(listItem);

        listItem.parentElement?.querySelector(
          `${this.listItemChevronSelector}[aria-controls="${listItem.querySelector(this.listSelector)?.id}"]`,
        )?.focus({ focusVisible: true });
      }
    }
  };

  /**
   * Toggles the open/closed state of a navigation list item.
   * @private
   * @param {HTMLLIElement} listItem The list item to toggle.
   * @returns {void}
   */
  _toggleListItemState = (listItem) => {
    const listItemChevron = listItem.querySelector(this.listItemChevronSelector);
    const listItemLevel = parseInt(listItem.getAttribute('data-nav-item-level'), 10);

    listItem.isOpen = !listItem.isOpen;

    if (listItem.isOpen) window.dispatchEvent(new CustomEvent(TOUJOU_EVENT_MAIN_NAV_SECOND_LEVEL_OPEN, { detail: { targetList: listItem.querySelector('.navigation') } }));

    listItemChevron?.setAttribute('aria-expanded', `${listItem.isOpen}`);
    listItemChevron?.setAttribute('aria-pressed', `${listItem.isOpen}`);

    if (listItem.isOpen) {
      listItem.setAttribute(this.isOpenAttribute, '');

      this._closeOtherOpenListItems(listItem);
      this.activeSubNavs[listItemLevel - 1] = listItem;
      window.addEventListener('click', this._onWindowClick);
      this.navEl.addEventListener('focusout', this._onFocusOut);
    } else {
      listItem.removeAttribute(this.isOpenAttribute);
      this.activeSubNavs[listItemLevel - 1] = null;
      if (this.activeSubNavs.length === listItemLevel) {
        this.activeSubNavs.pop();
      }
    }
  };

  /**
   * Closes other open sub-navigation items at the same level as the provided list item.
   * @private
   * @param {HTMLLIElement} listItem The list item to check against its siblings.
   * @returns {void}
   */
  _closeOtherOpenListItems = (listItem) => {
    const openSiblings = listItem.parentNode?.querySelectorAll(`${this.listItemSelector}[${this.isOpenAttribute}]`);

    if (!openSiblings) return;

    openSiblings.forEach((sibling) => {
      if (sibling.isOpen && sibling !== listItem) this._toggleListItemState(sibling);
    });
  };

  /**
   * Closes all open navigation list items.
   * @private
   * @returns {void}
   */
  _closeAllNavListItems = () => {
    this.navListItems.forEach((listItem) => {
      if (listItem.isOpen) {
        this._toggleListItemState(listItem);
        window.removeEventListener('click', this._onWindowClick);
        this.navEl.removeEventListener('focusout', this._onFocusOut);
      }
    });
  };

  /**
   * Handles a click event on the window to close the menu if the click is outside the navigation.
   * @private
   * @param {MouseEvent} event The click event object.
   * @returns {void}
   */
  _onWindowClick = (event) => {
    if (event.target?.closest(this.navListSelector) || event.target?.closest(this.navWrapSelector)) return;

    this._closeAllNavListItems();
  };

  /**
   * Handles focusout event on navEl to close all open menus if the element getting focus is outside navEl,
   * or is, on mobile viewports, not burgerEl
   * @param {FocusEvent} event The event object
   * @returns {void}
   */
  _onFocusOut = (event) => {
    if (!event.relatedTarget) return;
    if (event.relatedTarget.closest(this.navListSelector)) return;
    if (!isDesktop(this.minWidthDesktop) && event.relatedTarget === this.burgerEl) return;

    this._closeAllNavListItems();
  };
}

/**
 * Initializes the main navigation by creating a new Nav instance.
 * @returns {void}
 */
export function initMainNav() {
  const mainNavEl = document.querySelector('.wrap--navigation');

  if (!mainNavEl || mainNavEl.mainNav) return;

  mainNavEl.mainNav = new Nav(mainNavEl);
}
